import React, { useMemo } from 'react'
import styled from 'styled-components'
import ReactECharts from 'echarts-for-react'
import type { TFunction } from 'i18next'
import type { IDCDistributionBin } from '@popety-io/types'

interface IDCDistributionChartProps {
  distribution: IDCDistributionBin[]
  buildingIDC: number
  scopeLabel: string
  t: TFunction
}

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  margin-top: 16px;

  @media (max-width: 768px) {
    height: 250px;
  }
`

const ChartTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`

export const IDCDistributionChart: React.FC<IDCDistributionChartProps> = ({
  distribution,
  buildingIDC,
  scopeLabel,
  t,
}) => {
  // ECharts configuration
  const chartOptions = useMemo(() => {
    if (!distribution || distribution.length === 0) {
      return {}
    }

    // Filter data to only show bins from 0 to 1000
    const filteredDistribution = distribution.filter(
      (bin) => bin.bin_start >= 0 && bin.bin_start <= 1000
    )

    // Prepare data for area chart
    const xAxisData = filteredDistribution.map((bin) => bin.bin_start)
    const seriesData = filteredDistribution.map((bin) => bin.count)

    // Find the bin where the building's IDC falls
    const buildingBinIndex = filteredDistribution.findIndex(
      (bin) => buildingIDC >= bin.bin_start && buildingIDC < bin.bin_end
    )

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const binStart = params[0].axisValue
          const binEnd = binStart + 50
          const count = params[0].value
          return `<strong>${binStart} - ${binEnd} MJ/m²</strong><br/>
                  ${t('idcDistributionChart.tooltipBuildings', { count })}`
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '50px',
        top: '40px',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        name: t('idcDistributionChart.xAxisName'),
        nameLocation: 'middle',
        nameGap: 30,
        axisLabel: {
          formatter: (value: number) => `${value}`,
          rotate: 45,
        },
      },
      yAxis: {
        type: 'value',
        name: t('idcDistributionChart.yAxisName'),
        nameLocation: 'middle',
        nameGap: 50,
        axisLabel: {
          formatter: '{value}',
        },
      },
      series: [
        {
          name: 'Distribution',
          type: 'line',
          data: seriesData,
          smooth: true,
          showSymbol: false,
          lineStyle: {
            width: 2,
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: '#10b981' }, // Green at 0
                { offset: 0.45, color: '#fbbf24' }, // Yellow at 450
                { offset: 0.55, color: '#f97316' }, // Orange at 550
                { offset: 1, color: '#ef4444' }, // Red at 1000
              ],
            },
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: 'rgba(16, 185, 129, 0.4)' }, // Green at 0
                { offset: 0.45, color: 'rgba(251, 191, 36, 0.4)' }, // Yellow at 450
                { offset: 0.55, color: 'rgba(249, 115, 22, 0.4)' }, // Orange at 550
                { offset: 1, color: 'rgba(239, 68, 68, 0.4)' }, // Red at 1000
              ],
            },
          },
          markLine:
            buildingBinIndex >= 0
              ? {
                  symbol: ['none', 'none'],
                  label: {
                    formatter: t('idcDistributionChart.yourBuilding'),
                    position: 'end',
                  },
                  lineStyle: {
                    color: '#3b82f6',
                    width: 2,
                    type: 'solid',
                  },
                  data: [
                    {
                      xAxis: buildingBinIndex,
                    },
                  ],
                }
              : undefined,
        },
      ],
    }
  }, [distribution, buildingIDC, t])

  if (!distribution || distribution.length === 0) {
    return null
  }

  return (
    <>
      <ChartTitle>
        {t('idcDistributionChart.title', { scope: scopeLabel })}
      </ChartTitle>
      <ChartContainer>
        <ReactECharts
          option={chartOptions}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      </ChartContainer>
    </>
  )
}
